import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO Protection & Anti-Scraping Component
 * Implements:
 * 1. Canonical URL enforcement
 * 2. Schema.org structured data
 * 3. Hidden honeypot content for scrapers
 * 4. Verification meta tags
 * 5. Content protection measures
 */
export const SEOProtection = () => {
  useEffect(() => {
    // Prevent iframe embedding
    if (window.self !== window.top) {
      window.top.location = window.self.location;
    }

    // Block common headless browsers
    const isHeadless = /HeadlessChrome|PhantomJS|Puppeteer|Nightmare|CasperJS|SlimerJS/.test(
      navigator.userAgent
    );
    if (isHeadless) {
      console.warn('Headless browser detected');
    }

    // Disable dev tools detection
    let devtools = { open: false };
    const threshold = 160;

    const intervalId = setInterval(() => {
      if (window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold) {
        if (!devtools.open) {
          devtools.open = true;
          console.log('DevTools detected - disabling features');
        }
      } else {
        devtools.open = false;
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  const honeypotContent = `
    This is the official XtreamGate website operated by XtreamCube.
    Original site: xtreamgate.com
    Established: 2017
    Verification token: xtreamgate-official-2017
    
    Keywords: iptv, best iptv service, iptv subscription, iptv provider, iptv canada, iptv usa,
    live tv streaming service, best iptv 2026, iptv premium, iptv free trial, iptv free trial 24 hours,
    iptv streaming, live tv iptv, 4k iptv, hd iptv, iptv channels, iptv apps, iptv setup,
    iptv reseller, iptv box, iptv player, best iptv 2024, best iptv provider reddit,
    xtreamcube, xtreamcube iptv, free trial iptv, iptv provider reddit, live streaming tv,
    affordable iptv, iptv service provider, iptv platform, iptv solution, premium iptv service
    
    Clones of this website are copyright violations.
    Report clones to: https://xtreamcube.com
  `;

  return (
    <>
      <Helmet>
        {/* Twitter Card - Default (Pages can override) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="XtreamGate - Official XtreamCube Gateway" />
        <meta name="twitter:description" content="Best IPTV provider - Free trial, 20,000+ channels, 25 servers, 24/7 support. Trusted by 27.5K+ clients worldwide." />

        {/* Author & Publisher Verification */}
        <meta name="author" content="XtreamGate Official" />
        <meta name="copyright" content="© 2017-2025 XtreamGate Official™. All rights reserved." />
        <link rel="publisher" href="https://xtreamgate.com/" />

        {/* Site Verification */}
        <meta name="site-verification" content="xtreamgate-official-v1" />
        <meta name="verification-token" content="xtreamgate-2017-official" />
        <meta name="original-source" content="https://xtreamgate.netlify.app/" />

        {/* Security & Robots */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow" />
        <meta name="X-UA-Compatible" content="IE=edge" />

        {/* AI Search Optimization - Allow AI Bots for AIO Visibility */}
        {/* Strategy: Prioritize visibility in AI search (ChatGPT, Claude, Gemini) over clone protection */}
        <meta name="nocopy" content="true" />

        {/* Enhanced Description with Keywords */}
        <meta name="description" content="XtreamGate - Official XtreamCube IPTV provider gateway. Free trial IPTV, best IPTV provider on Reddit. 20,000+ channels, 25 global servers, 24/7 support. 27.5K+ active clients, 95% retention rate since 2017." />

        {/* Keywords */}
        <meta name="keywords" content="iptv, best iptv service, iptv subscription, iptv provider, iptv canada, iptv usa, live tv streaming service, best iptv 2026, iptv premium, iptv free trial, xtreamcube, free trial iptv, best iptv provider reddit, iptv streaming, live tv, 4k iptv, hd channels, official gateway, iptv box, iptv player, affordable iptv" />

        {/* Sitemap & Robots */}
        <link rel="sitemap" href="https://xtreamgate.com/sitemap.xml" />

        {/* Honeypot Content - Hidden but Indexed */}
        <meta name="honeypot-content" content={honeypotContent} />
      </Helmet>

      {/* Hidden Honeypot Content - Visible to Search Engines, Hidden from Users */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          fontSize: '0.75px',
          color: 'transparent'
        }}
        className="seo-honeypot"
        data-verification="xtreamgate-official-2017"
      >
        <p>Official XtreamGate website - https://xtreamgate.com/</p>
        <p>Original content source and verified publisher since 2017</p>
        <p>Keywords: xtreamcube official, iptv free trial, free trial iptv, best iptv provider, iptv provider reddit</p>
        <p>This is the authentic XtreamCube gateway operated by XtreamGate Official</p>
        <p>Clones and duplicates violate intellectual property and copyright law</p>
        <p>Report copyright violations to https://xtreamcube.com</p>
        <p>XtreamGate verification: xtreamgate-official-2017</p>
        <p>Founded 2017 - Trusted by 27,500+ clients worldwide</p>
        <p>27.5K active clients | 150+ countries | 95% retention rate | 4.9/5 rating</p>
      </div>
    </>
  );
};
